import buildScreenHeaderItems from '@/components/buildScreenHeaderItems'
import UniversalWebView from '@/components/UniversalWebView'
import { getJwtToken } from '@/lib/api/jwtOAuthApi'
import useWebBackButton from '@/lib/hooks/useWebBackButton'
import { useAppSelector } from '@/redux/store'
import { router, Stack } from 'expo-router'
import { useEffect, useState } from 'react'
import { Linking, Platform, StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { CHAT_CAPS, handleChatPurchase, type ChatPurchase } from '@/modules/handleChatPurchase'

// Namespace the chat stamps on every frame it posts (its `bridge-messages`
// contract); frames without it are not from the chat.
const BRIDGE_MESSAGE_SOURCE = 'cs-chat-sdk'

// The chat's own origin. Not an env var: every environment's App talks to the
// production chat, which is where the conversations and the agent live. Point it
// somewhere else here when a local one needs testing against.
const CS_CHAT_BASE_URL = 'https://jiri.jinshuju.net/cs'

// Anything this page may receive from the webview: the chat's bridge envelope,
// or the `openUrl` frame the injected anchor interceptor below posts.
type CustomerServiceMessage =
  | { source?: undefined; type: 'openUrl'; payload: string }
  | {
      source: typeof BRIDGE_MESSAGE_SOURCE
      v?: number
      type: string
      id: string
      data?: ChatPurchase & { conversationId?: string }
    }

export default function CustomerServicePage() {
  const insets = useSafeAreaInsets()
  const [url, setUrl] = useState<string | null>(null)
  const headerLeftItems = useWebBackButton('/profile')
  const jiriMobile = useAppSelector(state => state.session.jiriMobile)

  useEffect(() => {
    ;(async () => {
      const token = await getJwtToken()
      if (!token) return

      // Only a reader who has Jiri gets the handoff card's button: without it the card points at Jiri in text.
      const caps = [CHAT_CAPS, jiriMobile && 'openJiriConversation'].filter(Boolean).join(',')
      const capsParam = caps ? `&caps=${caps}` : ''
      const customerServiceUrl = `${CS_CHAT_BASE_URL}/recent?utm_device=mobile&utm_source=gd_app&utm_medium=nextapp&m_t=${token}${capsParam}`
      setUrl(customerServiceUrl)
    })()
  }, [jiriMobile])

  if (!url) return null

  return (
    <View style={[styles.container, Platform.OS === 'android' && { paddingBottom: insets.bottom }]}>
      <Stack.Screen
        options={{
          headerTransparent: false,
          ...buildScreenHeaderItems({ left: headerLeftItems }),
        }}
      />
      <UniversalWebView<CustomerServiceMessage>
        source={{ uri: url }}
        style={styles.webview}
        injectedJavaScript={`
            (function() {
              window.addEventListener('click', function(event) {
                if (event.target.tagName !== 'A' || !event.target.href) {
                  return;
                }
                window.ReactNativeWebView.postMessage(JSON.stringify({
                  payload: event.target.href,
                  type: 'openUrl'
                }));

                event.preventDefault();
                event.stopPropagation();
              }, false);
            })();
            true;
          `}
        onMessage={data => {
          if (!data) return

          try {
            // Two senders share this channel. The chat speaks the bridge
            // envelope and stamps `source`; the anchor interceptor injected
            // above is ours and stamps nothing — so the envelope is recognised
            // by `source`, and everything else stays on the legacy path that
            // makes every link in the page tappable.
            if (data.source === BRIDGE_MESSAGE_SOURCE) {
              // Must-ignore: a type this build does not know is not an error.
              if (data.type === 'purchase' && data.data) {
                handleChatPurchase(data.data)
              }
              // The handoff card made a Jiri conversation whose first message is waiting; Jiri sends it on open.
              if (data.type === 'openJiriConversation' && data.data?.conversationId) {
                router.push({ pathname: '/jiri', params: { conversationId: data.data.conversationId } })
              }
              return
            }

            if (data.type === 'openUrl') {
              Linking.openURL(data.payload)
            }
          } catch (error) {
            console.error('Failed to parse WebView message:', error)
          }
        }}
        {...(Platform.OS === 'android' && {
          geolocationEnabled: true,
        })}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  webview: {
    flex: 1,
  },
})
